import { FormField, Radio, RadioGroup, INDICATION_TYPES } from 'taboola-ultimate-ui';
import { withIndication } from 'modules/errors/components/withIndication';
import { useFormFieldValue } from 'modules/taboola-common-frontend-modules/formData';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { useFormValidatedValue } from 'modules/taboola-common-frontend-modules/validations';
import { PLATFORMS, TARGETING_TYPES } from '../../config';
import { InventoryTypeTargetingTooltip } from './InventoryTypeTargetingTooltip';
import { INVENTORY_TYPE_TARGETING } from './inventoryTypeTargetingConsts';
import commonStyles from '../commonEditor.module.scss';
import styles from './inventoryTypeTargeting.module.scss';

const validations = [
    {
        validationFn: (inventoryTypeTargetingType, options, { platformTargeting, inventoryTypeTargetingValues }) => {
            const isDesktopOnly = platformTargeting?.length === 1 && platformTargeting[0] === PLATFORMS.DESKTOP;
            const isTargetingApp =
                inventoryTypeTargetingType === TARGETING_TYPES.INCLUDE &&
                inventoryTypeTargetingValues?.[0] === INVENTORY_TYPE_TARGETING.APP;

            return !(isDesktopOnly && isTargetingApp);
        },
        messageId: 'validations.warning.inventory.type.desktop.app.warning',
        defaultMessage: 'Reach will be very limited when targeting Desktop and App traffic only.',
        severity: INDICATION_TYPES.WARNING,
    },
];

const RadioGroupWithIndication = withIndication(RadioGroup);

export const InventoryTypeTargeting = () => {
    const { value: inventoryTypeTargetingValues = [], onChange: onValuesChange } = useFormFieldValue({
        field: 'campaignTargeting.inventoryTypeTargeting.values',
    });

    const { value: platformTargeting } = useFormFieldValue({ field: 'platformTargeting' });

    const validationDependencies = {
        platformTargeting,
        inventoryTypeTargetingValues,
    };

    const {
        value: inventoryTypeTargetingType = TARGETING_TYPES.ALL,
        onChange: onTypeChange,
        indicationData,
        scrollRef,
    } = useFormValidatedValue({
        field: 'campaignTargeting.inventoryTypeTargeting.type',
        validations,
        validationDependencies,
    });

    const { indicationType } = indicationData;

    // Convert the current structure to the new one for backward compatibility
    const currentValue =
        inventoryTypeTargetingType === TARGETING_TYPES.ALL
            ? INVENTORY_TYPE_TARGETING.ALL
            : inventoryTypeTargetingValues[0] || INVENTORY_TYPE_TARGETING.ALL;

    const handleValueChange = value => {
        if (value === INVENTORY_TYPE_TARGETING.ALL) {
            onTypeChange(TARGETING_TYPES.ALL);
            onValuesChange([]);
        } else {
            onTypeChange(TARGETING_TYPES.INCLUDE);
            onValuesChange([value]);
        }
    };

    return (
        <FormField
            containerClass={styles['input']}
            label={
                <FormattedMessage
                    id="campaign.targeting.platform.inventory.type.label"
                    defaultMessage="Inventory Type"
                />
            }
            description={
                <FormattedMessage
                    id="campaign.targeting.platform.inventory.type.description"
                    defaultMessage="Select the type of inventory you want to target to optimize your campaign performance."
                />
            }
            helpText={<InventoryTypeTargetingTooltip />}
            ref={scrollRef}
        >
            <RadioGroupWithIndication
                name="inventoryTypeTargeting"
                selectedValue={currentValue}
                onChange={handleValueChange}
                className={styles['radio-group']}
                data-testid="inventory-type-targeting-radio-group"
                indicationContainerClass={
                    indicationType === INDICATION_TYPES.WARNING ? commonStyles['warning-indication'] : null
                }
                {...indicationData}
            >
                <Radio
                    value={INVENTORY_TYPE_TARGETING.ALL}
                    title={
                        <FormattedMessage id="campaign.targeting.platform.inventory.type.all" defaultMessage="All" />
                    }
                />
                <Radio
                    value={INVENTORY_TYPE_TARGETING.WEB}
                    title={
                        <FormattedMessage id="campaign.targeting.platform.inventory.type.WEB" defaultMessage="Web" />
                    }
                />
                <Radio
                    value={INVENTORY_TYPE_TARGETING.APP}
                    title={
                        <FormattedMessage id="campaign.targeting.platform.inventory.type.APP" defaultMessage="App" />
                    }
                />
            </RadioGroupWithIndication>
        </FormField>
    );
};
